/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import React, { useCallback, useEffect, useState } from 'react';

import { cypressId } from '../../../../../utils/cypress-attribute';
import { Logger } from '../../../../../utils/logger';
import { UiIcon } from '../../../../common/icons/ui-icon';
import { ShowIf } from '../../../../common/show-if/show-if';
import { useCodemirrorReferenceContext } from '../../../change-content-context/codemirror-reference-context';
import { useChangeEditorContentCallback } from '../../../change-content-context/use-change-editor-content-callback';
import { Button, Card, Form, InputGroup, Modal } from 'react-bootstrap';
import {
  Images as IconCustomUpload,
  Search as IconSearch
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { addCustomImageLink } from '../formatters/add-link';
import EditImageModal from './edit-image-modal';
import { useDebounce } from '../../../../../hooks/common/use-debounce';
import { WaitSpinner } from '../../../../common/wait-spinner/wait-spinner';
import { imageHelperApi } from '../../../../../services/imageHelper';
import { useUiNotifications } from '../../../../notifications/ui-notification-boundary';
import { IMAGE_PER_PAGE } from '../utils';

const logger = new Logger('Upload image button');

export type ImageDetails = {
  id: string;
  type: string;
  attributes: {
    filename: string;
    url: string;
    content_type: string;
    tags: any[];
  };
};

/**
 * Shows a button that uploads a chosen file to the backend and adds the link to the note.
 * Shows a edit button that replace current chosen file from uploaded file.
 */
export const UploadCustomImageButton: React.FC = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showEditImageModal, setShowEditImageModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [uploadImage, setUploadImage] = useState<File | undefined>();
  const changeEditorContent = useChangeEditorContentCallback();
  const [codeMirror] = useCodemirrorReferenceContext();
  const debouncedSearchTerm = useDebounce(searchValue, 1000);
  const { dispatchUiNotification } = useUiNotifications();
  const [
    imageLibraryTrigger,
    {
      isLoading: imageLibraryLoader,
      status: imageLibraryStatus,
      error: imageLibraryError,
      data: imagesObj,
      isFetching: imageLibraryFetch
    }
  ] = imageHelperApi.endpoints.getImageLibrary.useLazyQuery();
  const [
    imageDataTrigger,
    {
      isLoading: imageDataLoader,
      data: singleImageObj,
      isFetching: imageDataFetch,
      status: imageDataStatus,
      error: imageDataError
    }
  ] = imageHelperApi.endpoints.getImageDataById.useLazyQuery();

  const getImageLibrary = useCallback(
    (currentPage: number = 1, search: string = '') => {
      imageLibraryTrigger(
        {
          currentPage,
          search,
          perPage: IMAGE_PER_PAGE
        },
        true
      );
    },
    []
  );

  const debouncedHandleChange = useCallback(
    () => getImageLibrary(1, debouncedSearchTerm),
    [debouncedSearchTerm]
  );

  useEffect(() => {
    if (debouncedSearchTerm) {
      debouncedHandleChange();
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (imageLibraryStatus === 'uninitialized') return;
    if (imageLibraryStatus === 'rejected') {
      dispatchUiNotification(
        'Image Library Failed',
        imageLibraryError?.data?.error,
        {}
      );
    }
  }, [imageLibraryStatus, imageLibraryError, dispatchUiNotification]);

  useEffect(() => {
    if (imageDataStatus === 'uninitialized') return;
    if (imageDataStatus === 'rejected') {
      handleEditClose(false);
      dispatchUiNotification(
        'Image Data Fetch Failed',
        imageDataError?.data?.error,
        {}
      );
    }
  }, [imageDataStatus, imageDataError, dispatchUiNotification]);

  const handleChange = useCallback((e) => {
    const query: string = e.target.value.trim();
    if (query === '') {
      getImageLibrary();
    }
    setSearchValue(query);
  }, []);

  const addImage = useCallback(
    (url, name) => {
      const replacement = `![${name ?? ''}](${url})`;
      changeEditorContent?.(({ currentSelection, markdownContent }) =>
        addCustomImageLink(markdownContent, currentSelection, replacement)
      );
      setShowModal(false);
    },
    [changeEditorContent]
  );

  const handleEditClose = useCallback((isOpen: boolean) => {
    setShowEditImageModal(isOpen);
    setShowModal(!isOpen);
    setUploadImage(undefined);
  }, []);

  const handleEdit = useCallback((id: string) => {
    imageDataTrigger(id);
    handleEditClose(true);
  }, []);

  return (
    <>
      <Button
        variant='light'
        onClick={() => {
          setShowModal(true);
          getImageLibrary();
        }}
        disabled={!codeMirror}
        title={t('editor.editorToolbar.imageLibrary') ?? undefined}
        {...cypressId('editor-toolbar-upload-image-button')}>
        <UiIcon icon={IconCustomUpload} />
      </Button>
      <ShowIf condition={!!codeMirror}>
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          animation={true}
          dialogClassName={`text-dark`}
          size='xl'>
          <Modal.Header closeButton={true}>
            <Modal.Title>Choose Image</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <InputGroup size='lg' className='mb-3'>
              <InputGroup.Text id='basic-addon1'>
                <UiIcon icon={IconSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder='Search Image'
                aria-label='Search Image'
                aria-describedby='basic-addon1'
                onChange={handleChange}
              />
            </InputGroup>
            {imageLibraryLoader || imageLibraryFetch ? (
              <WaitSpinner size='30rem' />
            ) : (
              <>
                <div
                  style={{ gap: '1rem' }}
                  className='d-flex flex-wrap justify-content-between'>
                  {imagesObj?.data.length > 0
                    ? imagesObj?.data.map(
                        (imageData: ImageDetails, index: number) => (
                          <Card
                            key={`image-${index}`}
                            style={{ width: '25rem' }}
                            className='d-flex flex-column justify-content-between'>
                            <Card.Img
                              variant='top'
                              style={{ objectFit: 'contain' }}
                              src={imageData.attributes.url}
                              height='260rem'
                            />
                            <div className='d-flex flex-column'>
                              <Card.Text>
                                {imageData.attributes.filename}
                              </Card.Text>
                              <Button
                                onClick={() =>
                                  addImage(
                                    imageData.attributes.url,
                                    imageData.attributes.filename
                                  )
                                }
                                variant='primary'>
                                Add Image
                              </Button>
                              <Button
                                className='mt-2'
                                onClick={() => handleEdit(imageData.id)}
                                variant='secondary'>
                                Edit Image
                              </Button>
                            </div>
                          </Card>
                        )
                      )
                    : 'No image found'}
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </ShowIf>
      <EditImageModal
        showEditImageModal={showEditImageModal}
        handleEditClose={handleEditClose}
        uploadImage={uploadImage}
        setUploadImage={setUploadImage}
        imageFile={singleImageObj}
        isLoading={imageDataLoader || imageDataFetch}
      />
    </>
  );
};
